// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { ConfusionMatrix } from '@tls/sw91-communication/types/com.api_predictor';
import { Colors } from '@tls/treact-ui';
import { ProjectVersion } from 'model/ProjectMetaMessageExtensions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface Props {
  version: ProjectVersion;
  confusionMatrix?: ConfusionMatrix;
}

const EvaluationConfusionMatrix = ({ version, confusionMatrix }: Props) => {
  const { t } = useTranslation();
  if (!confusionMatrix) {
    return <Wrapper></Wrapper>;
  }
  const { tp, tn, fp, fn } = confusionMatrix;
  return (
    <Wrapper>
      <LeftLabel>{t('project.evaluation.qc.confusionMatrix.predicted')}</LeftLabel>
      <InnerWrapper>
        <TopLabel>{t('project.evaluation.qc.confusionMatrix.actual')}</TopLabel>
        <Grid>
          <Empty />
          <Header>{t('project.evaluation.qc.confusionMatrix.bad')}</Header>
          <Header>{t('project.evaluation.qc.confusionMatrix.good')}</Header>

          <RowLabel>{t('project.evaluation.qc.confusionMatrix.bad')}</RowLabel>
          <Cell
            $backgroundColor={Colors.trgreen.hex}
            data-testid={`evaluation-algorithm-${version}-confusion-matrix-tp-value`}
          >
            {tp}
          </Cell>
          <Cell
            $backgroundColor={Colors.trred2.hex}
            data-testid={`evaluation-algorithm-${version}-confusion-matrix-fp-value`}
          >
            {fp}
          </Cell>

          <RowLabel>{t('project.evaluation.qc.confusionMatrix.good')}</RowLabel>
          <Cell
            $backgroundColor={Colors.tryellow.hex}
            $textColor='black'
            data-testid={`evaluation-algorithm-${version}-confusion-matrix-fn-value`}
          >
            {fn}
          </Cell>
          <Cell
            $backgroundColor={Colors.trgreen.hex}
            data-testid={`evaluation-algorithm-${version}-confusion-matrix-tn-value`}
          >
            {tn}
          </Cell>
        </Grid>
      </InnerWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex: 1;
  font-family: sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: calc(100% - 6rem);
  margin-right: 6rem;
`;
const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 4rem 4rem 4rem;
  gap: 0.5rem;
`;

const Empty = styled.div``;
const TopLabel = styled.div`
  margin-left: 4rem;
  width: 8rem;
  text-align: center;
  border-bottom: 1px solid ${Colors.trgrey5.hex};
`;
const LeftLabel = styled.div`
  margin-top: 4rem;
  height: 8rem;
  border-right: 1px solid ${Colors.trgrey5.hex};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 0.5rem;
  margin-right: 0.5rem;
`;

const Header = styled.div`
  text-align: center;
  align-self: end;
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
`;

interface CellProps {
  $backgroundColor: string;
  $textColor?: string;
}

const Cell = styled.div<CellProps>`
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${props => props.$textColor ?? Colors.trwhite.hex};
  background-color: ${props => props.$backgroundColor};
  width: 4rem;
  height: 4rem;
`;

export default EvaluationConfusionMatrix;
